import { useContext, useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { loadLanguagePreference } from "../../redux/modules/language/actions";
import { loadUser } from "../../redux/modules/auth/actions";
import { getChecklist } from "../../redux/modules/checklist/actions";
import ModalSettings from "../../newUXComponents/ModalSettings";
import { useIntl } from "react-intl";
import { DialogContext } from "../../hooks/dialog/context";
import useNotification from "../../hooks/newUXNotification";
import { useLoading } from "../../hooks/loading";

import {
  Container,
  Title,
  Hr,
  ProgressContainer,
  ProgressBarStyles,
  NoChecklistContainer,
} from "./styles";
import ChecklistTable from "./ChecklistTable";
import {
  ChecklistInfoDescription,
  ChecklistInfoTitle,
  ToDoImage,
} from "../HomePage/styles";
import toDoImage from "../HomePage/assets/to_do_image.png";
import ChecklistDrawer from "./CheckListDrawer";

const Checklist = () => {
  const [selectedStep, setSelectedStep] = useState();
  const [totalSteps, setTotalSteps] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(0);
  const [sortParams, setSortParams] = useState({
    sortBy: null,
    sortDirection: null,
  });
  const intl = useIntl();
  const { checklist, isEmpty } = useSelector((state) => state.checklist);
  const { event } = useSelector((state) => state.initialSettings);
  const { createDialog } = useContext(DialogContext);
  const dispatch = useDispatch();
  const notification = useNotification();
  const { accountCreated } = useSelector((state) => state.auth);
  const { handleLoading } = useLoading();

  const callBackErrorMessage = useCallback(
    (message) => {
      handleLoading(false);
      notification.displayError(
        intl.formatMessage({
          id: "api.error." + message,
        }),
        "error-get-checklist"
      );
    },
    [handleLoading, notification, intl]
  );

  const callBackSuccessChecklist = useCallback(() => {
    handleLoading(false);
  }, [handleLoading]);

  const handleGetChecklist = useCallback(() => {
    handleLoading(true);
    dispatch(
      getChecklist({
        selectedEvent: event.value,
        sortBy: sortParams.sortBy,
        sortOrder: sortParams.sortDirection,
        callbackSuccess: callBackSuccessChecklist,
        callbackError: callBackErrorMessage,
      })
    );
  }, [
    dispatch,
    event?.value,
    sortParams?.sortBy,
    sortParams?.sortDirection,
    callBackSuccessChecklist,
    callBackErrorMessage,
    handleLoading,
  ]);

  const handleTableChange = useCallback((params) => {
    const { sortBy, sortDirection } = params;

    if (!sortBy || !sortDirection) {
      setSortParams({
        sortBy: null,
        sortDirection: null,
      });
      return;
    }

    setSortParams({
      sortBy,
      sortDirection: sortDirection === "ASC" ? "asc" : "desc",
    });
  }, []);

  useEffect(() => {
    if (accountCreated) {
      if (!event || event.length === 0 || JSON.stringify(event) === "{}") {
        notification.close("event-not-selected-message");
        notification.displayInfo(
          intl.formatMessage({ id: "error.contract_not_selected" }),
          "event-not-selected-message"
        );
        dispatch(loadUser());
        dispatch(loadLanguagePreference());

        createDialog({
          id: "modal-settings-login",
          Component: ModalSettings,
          props: {},
        });
        return;
      } else notification.close("event-not-selected-message");
    }
  }, [accountCreated, event]);

  useEffect(() => {
    if (!event?.value) return;

    handleGetChecklist();
  }, [event?.value, sortParams]);

  useEffect(() => {
    if (!event.value) return;
    if (isEmpty) {
      notification.displayInfo(
        intl.formatMessage({ id: "checklist.empty_message" }),
        "empty-checklist-message"
      );
    } else notification.close("empty-checklist-message");
  }, [isEmpty]);

  useEffect(() => {
    if (!selectedStep) return;
    createDialog({
      id: "checklist-step-display",
      Component: ChecklistDrawer,
      props: { step: selectedStep },
    });
    setSelectedStep();
  }, [selectedStep]);

  useEffect(() => {
    if (checklist.length === 0) return;

    setTotalSteps(checklist.length);

    const mappedCompletedSteps = checklist.filter(
      (step) => step.status === "Concluded"
    );

    setCompletedSteps(mappedCompletedSteps.length);
  }, [checklist, setTotalSteps, setCompletedSteps]);

  const getPercentage = () => {
    return Math.round((completedSteps * 100) / totalSteps);
  };

  return (
    <Container>
      <Title>Checklist</Title>
      <Hr />
      {checklist.length > 0 ? (
        <>
          <ProgressContainer>
            <div className="progress-header">
              <div>
                <h3>{intl.formatMessage({ id: "your_progress" })}</h3>
                <span>
                  {intl.formatMessage(
                    { id: "checklist_progress_bar" },
                    { completedSteps: completedSteps, totalSteps: totalSteps }
                  )}
                </span>
              </div>

              <span className="percent">{getPercentage()}%</span>
            </div>

            <ProgressBarStyles
              strokeWidth={16}
              strokeColor={{
                "0%": "#002244",
                "100%": "#11A7D9",
              }}
              percent={getPercentage()}
              status={getPercentage() === 100 ? "success" : "active"}
              showInfo={false}
            />
          </ProgressContainer>
          <ChecklistTable
            data-test="checklist-table"
            items={checklist}
            onTableChange={handleTableChange}
          />
        </>
      ) : (
        <NoChecklistContainer>
          <ToDoImage src={toDoImage} alt="To Do Image" />
          <ChecklistInfoTitle>
            {intl.formatMessage({ id: "checklist_preparation" })}
          </ChecklistInfoTitle>
          <ChecklistInfoDescription>
            {intl.formatMessage({
              id: "checklist_preparation_sub",
            })}
          </ChecklistInfoDescription>
        </NoChecklistContainer>
      )}
    </Container>
  );
};

export default Checklist;
